import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Alert from '../common/Alert.js';
import UserProfileModal from '../common/UserProfileModal.js';
import userService from '../../services/UserService.js';
import logo from "../../static/images/logo.jpeg";
import { LogOut, Menu, Pencil, Cancel, X } from "lucide-react";

const Header = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, logout, loading } = useAuth();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const handleLogout = async () => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to logout from the system?',
      'Logout Confirmation'
    );

    if (confirmed) {
      await logout();
      navigate('/');
    }
  };

  const getProfileImageUrl = (userData = user) => {
    if (userData?.profile_photo_base64) {
      return `data:image/jpeg;base64,${userData.profile_photo_base64}`;
    }
    if (userData?.user_id) {
      return userService.getProfilePhoto(userData.user_id);
    }
    return null;
  };

  useEffect(() => {
    const handleToggle = (e) => {
      if (typeof e.detail === "boolean") {
        setIsSidebarOpen(e.detail);   // set exact state
      } else {
        setIsSidebarOpen(prev => !prev); // fallback
      }
    };

    window.addEventListener('toggle-sidebar', handleToggle);
    return () => window.removeEventListener('toggle-sidebar', handleToggle);
  }, []);


  const handleProfileClick = () => {
    setShowProfileModal(true);
  };

  const closeModal = () => {
    setShowProfileModal(false);
  };

  return (
    <>
      <header className="bg-headerbg shadow-sm border-b border-gray-200 fixed w-full lg:sticky top-0 z-50">
        <div 
          className="
            flex items-center px-3 sm:px-6 py-3 sm:py-2
            w-full
            sm:justify-between 
            justify-center
          "
        >
          {/* LEFT EMPTY - visible only on mobile */}
          <div className="flex-1 lg:hidden flex items-center">
            <button
              onClick={() => window.dispatchEvent(new Event('toggle-sidebar'))}
              className="p-2 relative w-10 h-10 flex items-center justify-center"
              aria-label="Toggle Menu"
            >
              {/* Menu Icon */}
              <Menu
                size={22}
                className={`absolute transition-all duration-300 ease-in-out text-buttonBg ${
                  isSidebarOpen
                    ? 'opacity-0 scale-75 rotate-90'
                    : 'opacity-100 scale-100 rotate-0'
                }`}
              />

              {/* X Icon */}
              <X
                size={22}
                className={`absolute transition-all duration-300 ease-in-out text-buttonBg ${
                  isSidebarOpen
                    ? 'opacity-100 scale-100 rotate-0'
                    : 'opacity-0 scale-75 -rotate-90'
                }`}
              />
            </button>


          </div>

          {/* CENTER LOGO */}
          <div className="flex-1 flex justify-center sm:justify-start items-center gap-2">
            {/* Desktop Logo */}
            <h1 className="hidden lg:block">
              <img src={logo} alt="Logo" className="h-14 sm:h-14 w-auto" />
            </h1>

            {/* Mobile Logo */}
            <h1 className="lg:hidden">
              {/* 🔧 FIX: absolute हटाया, बाकी सब same */}
              <img
                src={logo}
                alt="Logo"
                className="h-10 w-auto"
              />
            </h1>
          </div>

          {/* RIGHT SECTION – PROFILE + LOGOUT */}
          <div className="flex-1 flex justify-end items-center gap-2 sm:gap-4">
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                <div className="space-y-1 hidden sm:block">
                  <div className="w-24 h-3 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-16 h-2 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            ) : user ? (
              <>
                {/* Profile Button */}
                <button
                  onClick={handleProfileClick}
                  className="flex items-center gap-2 sm:gap-3 hover:bg-gray-50 rounded-lg p-1 sm:p-2 transition-colors"
                >
                  <div className="relative">
                    {getProfileImageUrl() ? (
                      <img
                        src={getProfileImageUrl()}
                        alt={user.name}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center border-2 border-gray-200">
                        <span className="text-gray-600 font-bold text-xs sm:text-sm">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Desktop Username */}
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-dark">{user.name}</p>
                    <p className="text-xs text-secondary">{user.role}</p>
                  </div>
                </button>

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="p-1.5 sm:p-2 hover:bg-red-50 text-danger rounded-md transition-colors"
                  title="Logout from System"
                >
                  <LogOut size={16} className="text-buttonBg" />
                </button>
              </>
            ) : null}
          </div>
        </div>
      </header>

      {/* User Profile Modal */}
      {showProfileModal && (
        <UserProfileModal 
          isOpen={showProfileModal}
          onClose={closeModal}
          userId={user?.user_id}
        />
      )}
    </>
  );
};

export default Header;
